import { useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Loader2, CheckCircle2, ArrowLeft, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("forgot.errorGeneric"));
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4 py-12 relative">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div data-testid="forgot-password-page" className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-zinc-100">
          <div className="w-12 h-12 rounded-md bg-zinc-100 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-zinc-700" />
          </div>
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">PromoCrew</div>
            <div className="font-display font-bold text-lg text-zinc-950">{t("forgot.title")}</div>
          </div>
        </div>

        {sent ? (
          <div data-testid="forgot-password-sent" className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <Trans i18nKey="forgot.sentTitle" ns="auth" values={{ email }} components={{ strong: <strong /> }} />
                {" "}
                <Trans i18nKey="forgot.sentExpiry" ns="auth" components={{ strong: <strong /> }} />
              </div>
            </div>
            <p className="text-xs text-zinc-500">{t("forgot.sentNote")}</p>
            <Link to="/login" className="text-sm text-zinc-700 hover:text-zinc-950 underline inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> {t("forgot.back")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-zinc-600">{t("forgot.help")}</p>
            {error && (
              <div data-testid="forgot-password-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{t("login.email")}</label>
              <input data-testid="forgot-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 text-sm" placeholder={t("login.emailPlaceholder")} />
            </div>
            <button data-testid="forgot-submit" type="submit" disabled={sending} className="w-full bg-zinc-950 hover:bg-zinc-800 text-white font-semibold py-2.5 rounded-lg disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {sending ? t("forgot.sending") : t("forgot.submit")}
            </button>
            <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> {t("forgot.back")}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
